import React, { useMemo } from 'react';
import { FaSnowflake,FaLeaf, FaUmbrellaBeach } from "react-icons/fa";
import { PiPottedPlantLight } from "react-icons/pi";

type Season = 'spring' | 'summer' | 'autumn' | 'winter';
type Hemisphere = 'north' | 'south';

export interface SeasonIconProps {
  /** Optional date; defaults to now */
  date?: Date;
  /** Hemisphere for mapping */
  hemisphere?: Hemisphere;
  /** Pools of icons per season; defaults to emoji arrays */
  icons?: Partial<Record<Season, React.ReactNode[]>>;
  /** Optional labels to render next to the icon */
  labels?: Partial<Record<Season, string>>;
  /** Show text label next to the icon */
  showLabel?: boolean;
  /** Pixel size for the icon (emoji via fontSize) */
  size?: number;
  /** Additional className */
  className?: string;
}

/**
 * Meteorological season by month and hemisphere.
 * Northern: Mar–May SPR, Jun–Aug SUM, Sep–Nov AUT, Dec–Feb WIN.
 * Southern: swap SPR↔AUT and SUM↔WIN.
 */
function getSeason(date: Date, hemisphere: Hemisphere): Season {
  const m = date.getMonth(); // 0..11
  let season: Season;

  if (m >= 2 && m <= 4) season = 'spring';      // Mar–May
  else if (m >= 5 && m <= 7) season = 'summer'; // Jun–Aug
  else if (m >= 8 && m <= 10) season = 'autumn';// Sep–Nov
  else season = 'winter';                        // Dec–Feb

  if (hemisphere === 'south') {
    const flip: Record<Season, Season> = {
      spring: 'autumn',
      summer: 'winter',
      autumn: 'spring',
      winter: 'summer',
    };
    season = flip[season];
  }
  return season;
}

const DEFAULT_ICONS: Record<Season, React.ReactNode> = {
  spring: <PiPottedPlantLight />,
  summer: <FaUmbrellaBeach />,
  autumn: <FaLeaf />,
  winter: <FaSnowflake />,
};

const DEFAULT_LABELS: Record<Season, string> = {
  spring: 'Spring',
  summer: 'Summer',
  autumn: 'Autumn',
  winter: 'Winter',
};

export const SeasonIcon: React.FC<SeasonIconProps> = ({
  date = new Date(),
  hemisphere = 'north',
  icons = {},
  labels = {},
  showLabel = false,
  size = 24,
  className,
}) => {
  
const season = getSeason(date, hemisphere);
  const iconSet = { ...DEFAULT_ICONS, ...icons };
  const labelSet = { ...DEFAULT_LABELS, ...labels };
  const icon = iconSet[season];
  const label = labelSet[season]

  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: size }}
      aria-label={label}
      title={label}
    >
      <span>{icon}</span>
      {showLabel && <span style={{ fontSize: Math.max(12, size * 0.6) }}>{label}</span>}
    </span>
  );
};
